<template>
  <div>
    <!-- Stat cards -->
    <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5 mb-6">
      <template v-if="loading">
        <div v-for="i in 4" :key="'skel-' + i" class="bg-white rounded-xl border border-gray-200/60 p-5">
          <LoadingBlock height="80" label="Loading..."/>
        </div>
      </template>
      <template v-else>
        <div
            v-for="(stat, i) in stats"
            :key="stat.label"
            :class="['animate-fade-in', `stagger-${i + 1}`]"
        >
          <div
              class="bg-white rounded-xl border border-gray-200/60 p-5 hover:shadow-md hover:shadow-gray-200/50 transition-shadow duration-300">
            <div class="flex items-center justify-between mb-4">
              <span class="text-sm font-medium text-gray-400 uppercase tracking-wider">{{ stat.label }}</span>
              <span
                  :class="['flex items-center justify-center w-9 h-9 rounded-lg', stat.iconBg]"
                  v-html="stat.icon"
              />
            </div>
            <div class="font-data text-[28px] font-medium text-gray-900 leading-none">
              {{ stat.value }}
            </div>
            <p v-if="stat.sub" class="text-sm text-gray-400 mt-2">{{ stat.sub }}</p>
          </div>
        </div>
      </template>
    </div>

    <!-- Two column layout -->
    <div class="grid grid-cols-1 xl:grid-cols-3 gap-6">
      <!-- Active Lives -->
      <div class="xl:col-span-2 bg-white rounded-xl border border-gray-200/60">
        <div class="flex items-center justify-between px-6 py-3 border-b border-gray-100">
          <div>
            <h2 class="text-[15px] font-semibold text-gray-900">Active Streams</h2>
            <p class="text-sm text-gray-400 mt-0.5">Currently live channels</p>
          </div>
          <router-link
              to="/lives"
              class="text-sm font-medium text-[var(--color-primary)] hover:underline"
          >
            View all
          </router-link>
        </div>

        <LoadingBlock v-if="loading" height="200" label="Loading streams..."/>
        <template v-else>
          <div v-if="activeLives.length" class="divide-y divide-gray-50 animate-fade-in max-h-[260px] overflow-y-auto rounded-b-xl">
            <div
                v-for="live in activeLives"
                :key="live.id"
                class="flex items-center gap-4 px-6 py-4 hover:bg-gray-50/50 transition-colors"
            >
              <div class="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center shrink-0">
                <svg class="w-4 h-4 text-green-500" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium text-gray-900 truncate">{{ live.meta.name }}</p>
                <p v-if="live.transcoding?.channel?.started_at" class="text-sm text-gray-400 font-data">Started at
                  {{ $filters.formatDatetime(live.transcoding?.channel?.started_at) }}</p>
              </div>
              <span class="badge-live">Live</span>
              <div class="flex items-center gap-1 shrink-0">
                <button @click="videoUrl = $mediaUrl.live(live._id)"
                        class="p-2 rounded-lg text-gray-400 hover:text-green-500 hover:bg-green-50 transition-colors"
                        title="Watch">
                  <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                       stroke-linecap="round" stroke-linejoin="round">
                    <rect x="2" y="7" width="20" height="15" rx="2" ry="2"/>
                    <polyline points="17 2 12 7 7 2"/>
                  </svg>
                </button>
                <router-link :to="{ name: 'editor', query: { lId: live._id } }"
                             class="p-2 rounded-lg text-gray-400 hover:text-violet-500 hover:bg-violet-50 transition-colors"
                             title="Open editor">
                  <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"
                       stroke-linecap="round" stroke-linejoin="round">
                    <rect x="2" y="8" width="20" height="14" rx="2"/>
                    <path d="M2 8l4-5h12l4 5"/>
                    <line x1="8" y1="3.5" x2="6.5" y2="8"/>
                    <line x1="14" y1="3.5" x2="12.5" y2="8"/>
                    <circle cx="17" cy="19" r="1.5"/>
                    <circle cx="21" cy="19" r="1.5"/>
                    <line x1="15.5" y1="19" x2="13" y2="15"/>
                    <line x1="22.5" y1="19" x2="13" y2="15"/>
                  </svg>
                </router-link>
              </div>
            </div>
          </div>

          <div v-else class="px-6 py-16 text-center animate-fade-in">
            <div class="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
              <svg class="w-5 h-5 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                   stroke-linecap="round" stroke-linejoin="round">
                <path
                    d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/>
              </svg>
            </div>
            <p class="text-sm text-gray-500 font-medium">No active streams</p>
            <p class="text-sm text-gray-400 mt-1">Start a livestream to see it here</p>
          </div>
        </template>
      </div>

      <!-- Usage / Quick stats -->
      <div class="bg-white rounded-xl border border-gray-200/60">
        <div class="px-6 py-3 border-b border-gray-100">
          <h2 class="text-[15px] font-semibold text-gray-900">Quick Stats</h2>
          <p class="text-sm text-gray-400 mt-0.5">Platform activity summary</p>
        </div>

        <LoadingBlock v-if="loading" height="200" label="Loading stats..."/>
        <div v-else class="p-6 space-y-5 animate-fade-in">
          <div v-for="item in quickStats" :key="item.label">
            <div class="flex items-center justify-between mb-2">
              <span class="text-sm text-gray-400">{{ item.label }}</span>
              <span class="text-sm font-data font-medium text-gray-400">{{ item.value }} <span
                  v-if="item.allowed"> / {{ item.allowed }}</span></span>
            </div>
            <div class="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                  class="h-full rounded-full transition-all duration-700 ease-out"
                  :class="barColor(item.percent)"
                  :style="{ width: item.percent + '%' }"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Weekly Schedule -->
    <div class="mt-6 bg-white rounded-xl border border-gray-200/60">
      <div class="px-6 py-3 border-b border-gray-100">
        <div class="flex items-start justify-between mb-3">
          <div>
            <h2 class="text-[15px] font-semibold text-gray-900">This Week's Schedule</h2>
            <p class="text-sm text-gray-400 mt-0.5">{{ weekRange }}</p>
          </div>
          <router-link to="/lives" class="text-sm font-medium text-[var(--color-primary)] hover:underline">Manage</router-link>
        </div>
      </div>
      <div class="flex px-4 py-2.5 gap-1.5 border-b border-gray-100 bg-gray-50/50">
        <button
          v-for="d in weekDays" :key="d.value"
          @click="selectDay(d.value)"
          :class="[
            'flex-1 flex flex-col items-center py-2.5 rounded-xl transition-all',
            selectedDay === d.value
              ? 'bg-[var(--color-primary)] text-white shadow-md shadow-[var(--color-primary-glow)]'
              : d.isToday
                ? 'bg-white text-[var(--color-primary)] border border-[var(--color-primary-glow)] shadow-sm'
                : 'text-gray-500 hover:bg-white hover:shadow-sm'
          ]"
        >
          <span class="text-[10px] font-semibold uppercase tracking-wider" :class="selectedDay === d.value ? 'text-white/70' : d.isToday ? 'text-[var(--color-primary)]' : 'text-gray-400'">{{ d.short }}</span>
          <span class="text-[15px] font-data font-bold mt-0.5">{{ d.date }}</span>
          <span v-if="d.isToday && selectedDay !== d.value" class="w-1 h-1 rounded-full bg-[var(--color-primary)] mt-1" />
        </button>
      </div>

      <div v-if="todaySchedules.length" :class="{ 'opacity-50 pointer-events-none': loadingSchedule }">
          <!-- Timezone info bar -->
          <div class="flex items-center gap-2 px-6 py-2.5 bg-blue-50/70 border-b border-blue-100/50">
            <svg class="w-4 h-4 text-blue-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            <p class="text-sm text-blue-500">Showing in <span class="font-medium">{{ localTzShort }}</span> · click times to toggle original timezone</p>
          </div>
          <div ref="scheduleScroll" class="divide-y divide-gray-50 max-h-[360px] overflow-y-auto rounded-b-xl">
            <div
              v-for="(schedule, i) in todaySchedules"
              :key="schedule._id"
              :data-airing="isCurrentlyAiring(schedule)"
              :class="['flex items-stretch transition-colors', isCurrentlyAiring(schedule) ? 'bg-green-50/50' : isSchedulePast(schedule) ? 'opacity-50' : 'hover:bg-gray-50/50']"
            >
              <!-- Time column -->
              <div class="shrink-0 flex items-stretch border-r border-gray-100 cursor-pointer select-none" @click="toggleOriginalTime(schedule._id)" :title="showOriginalTime[schedule._id] ? 'Click to show local time' : 'Click to show original time'">
                <div class="w-5 flex items-center justify-center" :class="showOriginalTime[schedule._id] ? 'bg-blue-50' : 'bg-gray-50'">
                  <span class="text-[9px] font-bold uppercase tracking-widest [writing-mode:vertical-lr] rotate-180" :class="showOriginalTime[schedule._id] ? 'text-blue-400' : 'text-gray-400'">{{ showOriginalTime[schedule._id] ? getShortTz(schedule.timezone || 'America/New_York') : localTzShort }}</span>
                </div>
                <div class="w-20 flex flex-col items-center justify-center py-4">
                  <span class="text-sm font-data font-semibold" :class="isCurrentlyAiring(schedule) ? 'text-green-600' : 'text-gray-900'">{{ showOriginalTime[schedule._id] ? formatOriginalTime(schedule, 'start') : formatScheduleTime(schedule, 'start') }}</span>
                  <span class="text-[10px] text-gray-400 mt-0.5">{{ showOriginalTime[schedule._id] ? formatOriginalTime(schedule, 'end') : formatScheduleTime(schedule, 'end') }}</span>
                </div>
              </div>
              <!-- Content -->
              <div class="flex-1 flex items-center gap-3 px-5 py-4 min-w-0">
                <div :class="['w-1 self-stretch rounded-full shrink-0', isCurrentlyAiring(schedule) ? 'bg-green-500' : isSchedulePast(schedule) ? 'bg-gray-200' : 'bg-[var(--color-primary)]']" />
                <div class="min-w-0 flex-1">
                  <p class="text-sm font-medium text-gray-900 truncate">{{ schedule.title }}</p>
                  <div class="flex items-center gap-2 mt-1">
                    <span class="text-xs text-gray-400 truncate">{{ getLiveName(schedule.live_id) }}</span>
                    <span v-if="schedule.recurrency === 'recurrent'" class="text-[10px] font-medium px-1.5 py-0.5 rounded bg-violet-50 text-violet-500">Recurrent</span>
                    <span v-else class="text-[10px] font-medium px-1.5 py-0.5 rounded bg-blue-50 text-blue-500">One-time</span>
                  </div>
                </div>
                <div class="flex items-center gap-2 shrink-0">
                  <span v-if="isCurrentlyAiring(schedule)" class="flex items-center gap-1.5 px-2 py-0.5 rounded bg-green-500/10 border border-green-200">
                    <span class="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                    <span class="text-[10px] font-bold text-green-600 uppercase">On Air</span>
                  </span>
                  <span v-if="schedule.enable_recording" class="text-[10px] font-medium px-1.5 py-0.5 rounded bg-red-50 text-red-500">REC</span>
                  <span class="text-xs font-data text-gray-400">{{ formatDuration(schedule) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

      <div v-else-if="!loadingSchedule" class="px-6 py-12 text-center animate-fade-in">
        <div class="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
          <svg class="w-5 h-5 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
        </div>
        <p class="text-sm text-gray-500 font-medium">No programs scheduled for {{ selectedDay }}</p>
        <p class="text-sm text-gray-400 mt-1">Set up schedules in your live channels</p>
      </div>
      <div v-else-if="loadingSchedule" class="px-6 py-12 text-center">
        <div class="cs-spinner mx-auto" />
      </div>
    </div>

    <!-- Video player modal -->
    <MediaPlayerModal
        :show="!!videoUrl"
        title="Live Stream"
        :hls-url="videoUrl || ''"
        :formats="[]"
        :live="true"
        @close="videoUrl = null"
    />
  </div>
</template>

<script>
import LoadingBlock from '../../components/LoadingBlock.vue'
import MediaPlayerModal from '../../components/MediaPlayerModal.vue'
import api from '../../services/api.js'

export default {
  name: 'DashboardView',
  components: { LoadingBlock, MediaPlayerModal },

  data () {
    return {
      loading: true,
      totalLives: 0,
      activeLivesCount: 0,
      totalMedias: 0,
      dataTransferred: {
        total: 0,
        allowed: 0
      },
      storageUsed: {
        total: 0,
        allowed: 0
      },
      activeLives: [],
      videoUrl: null,
      pollInterval: null,
      loadingSchedule: true,
      todaySchedules: [],
      livesMap: {},
      showOriginalTime: {},
      selectedDay: new Date().toLocaleDateString('en-US', { weekday: 'long' })
    }
  },

  computed: {
    todayLabel() {
      return new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
    },
    weekRange() {
      const today = new Date()
      const dayOfWeek = today.getDay()
      const mon = new Date(today)
      mon.setDate(today.getDate() - ((dayOfWeek + 6) % 7))
      const sun = new Date(mon)
      sun.setDate(mon.getDate() + 6)
      const fmt = { month: 'short', day: 'numeric' }
      return `${mon.toLocaleDateString('en-US', fmt)} – ${sun.toLocaleDateString('en-US', fmt)}`
    },
    weekDays() {
      const today = new Date()
      const dayOfWeek = today.getDay()
      const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
      const mondayOffset = (dayOfWeek + 6) % 7
      return days.map((name, i) => {
        const diff = i - mondayOffset
        const date = new Date(today)
        date.setDate(today.getDate() + diff)
        const isoDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
        return {
          value: name,
          short: name.slice(0, 3),
          date: date.getDate(),
          isoDate,
          isToday: diff === 0
        }
      })
    },
    selectedDate() {
      const day = this.weekDays.find(d => d.value === this.selectedDay)
      return day?.isoDate || ''
    },
    isToday() {
      const todayName = new Date().toLocaleDateString('en-US', { weekday: 'long' })
      return this.selectedDay === todayName
    },
    localTzShort() {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
        const short = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' }).formatToParts(new Date()).find(p => p.type === 'timeZoneName')
        return short?.value || tz
      } catch { return 'Local' }
    },
    stats () {
      return [
        {
          label: 'Total Lives',
          value: this.formatNumber(this.totalLives),
          sub: 'All configured streams',
          icon: '<svg class="w-4 h-4 text-blue-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>',
          iconBg: 'bg-blue-50'
        },
        {
          label: 'Active Lives',
          value: this.formatNumber(this.activeLivesCount),
          sub: 'Currently streaming',
          icon: '<svg class="w-4 h-4 text-green-500" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="4"/><path d="M16.24 7.76a6 6 0 010 8.49m-8.48-8.49a6 6 0 000 8.49M20.48 3.52a11 11 0 010 16.96m-16.96 0a11 11 0 010-16.96" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>',
          iconBg: 'bg-green-50'
        },
        {
          label: 'Total Medias',
          value: this.formatNumber(this.totalMedias),
          sub: 'Files in library',
          icon: '<svg class="w-4 h-4 text-violet-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>',
          iconBg: 'bg-violet-50'
        },
        {
          label: 'Storage Used',
          value: this.$filters.formatBytes(this.storageUsed.total),
          sub: 'Total disk usage',
          icon: '<svg class="w-4 h-4 text-amber-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/></svg>',
          iconBg: 'bg-amber-50'
        }
      ]
    },
    quickStats () {
      return [
        // {
        //   label: 'Stream uptime today',
        //   value: '—',
        //   percent: 0,
        //   barColor: 'bg-[var(--color-primary)]'
        // },
        {
          label: 'Data transferred this month',
          value: this.$filters.formatBytes(this.dataTransferred.total),
          allowed: this.$filters.formatBytes(this.dataTransferred.allowed),
          percent: Math.min(100, Math.ceil(this.dataTransferred.total * 100 / this.dataTransferred.allowed)),
          barColor: 'bg-green-500'
        },
        {
          label: 'Storage capacity',
          value: this.$filters.formatBytes(this.storageUsed.total),
          allowed: this.$filters.formatBytes(this.storageUsed.allowed),
          percent: Math.min(100, Math.ceil(this.storageUsed.total * 100 / this.storageUsed.allowed)),
          barColor: 'bg-amber-500'
        }
      ]
    }
  },

  async mounted () {
    await this.fetchData()
    this.scrollToAiringSchedule()
    this.pollInterval = setInterval(() => this.fetchData(true), 10000)
  },

  beforeUnmount () {
    if (this.pollInterval) clearInterval(this.pollInterval)
  },

  methods: {
    formatNumber(n) {
      if (n == null) return '0'
      return Number(n).toLocaleString('en-US')
    },

    barColor(percent) {
      if (percent >= 80) return 'bg-red-500'
      if (percent >= 50) return 'bg-amber-500'
      return 'bg-green-500'
    },

    async fetchData (silent = false) {
      try {
        const opts = silent ? { silent: true } : {}
        const [livesCountRes, mediasCountRes, activeStreamsRes, dataTransferred, storageUsed, schedulesRes, livesListRes] = await Promise.all([
          api.get('/livestreams/count', opts).catch(() => null),
          api.get('/medias/count', opts).catch(() => null),
          api.get('/livestreams', { ...opts, params: { limit: 25, page: 1, channel_status: 'streaming' } }).catch(() => null),
          api.get('/dashboard/data-transferred', opts).catch(() => null),
          api.get('/dashboard/storage', opts).catch(() => null),
          api.get('/schedulers/by-date', { ...opts, params: { date: this.selectedDate } }).catch(() => null),
          api.get('/livestreams', { ...opts, params: { limit: 100, format: 1 } }).catch(() => null)
        ])

        if (livesCountRes?.data) {
          this.totalLives = livesCountRes.data.total || 0
        }

        if (mediasCountRes?.data) {
          this.totalMedias = mediasCountRes.data.total || 0
        }

        if (activeStreamsRes?.data) {
          this.activeLives = activeStreamsRes.data.items || []
          this.activeLivesCount = this.activeLives.length
        }

        if (dataTransferred?.data) {
          this.dataTransferred.total = dataTransferred.data.total
          this.dataTransferred.allowed = dataTransferred.data.allowed
        }

        if (storageUsed?.data) {
          this.storageUsed.total = storageUsed.data.total
          this.storageUsed.allowed = storageUsed.data.allowed
        }

        if (livesListRes?.data) {
          const lives = livesListRes.data.items || []
          this.livesMap = Object.fromEntries(lives.map(l => [l._id?.toString(), l.name || l.title || l._id]))
        }

        if (schedulesRes?.data) {
          const items = schedulesRes.data.items || []
          items.sort((a, b) => {
            const aStart = this.scheduleTimeToLocal(a, 'start')
            const bStart = this.scheduleTimeToLocal(b, 'start')
            return (aStart?.getTime() || 0) - (bStart?.getTime() || 0)
          })
          this.todaySchedules = items
        }
      } catch {
        // Silently handle — dashboard should not break
      } finally {
        this.loading = false
        this.loadingSchedule = false
      }
    },

    async selectDay(day) {
      if (this.selectedDay === day) return
      this.selectedDay = day
      this.showOriginalTime = {}
      await this.fetchSchedule()
    },

    async fetchSchedule() {
      this.loadingSchedule = true
      try {
        const res = await api.get('/schedulers/by-date', { params: { date: this.selectedDate }, silent: true })
        const items = res.data?.items || []
        items.sort((a, b) => {
          const aStart = this.scheduleTimeToLocal(a, 'start')
          const bStart = this.scheduleTimeToLocal(b, 'start')
          return (aStart?.getTime() || 0) - (bStart?.getTime() || 0)
        })
        this.todaySchedules = items
      } catch { /* silent */ } finally {
        this.loadingSchedule = false
      }
      this.scrollToAiringSchedule()
    },

    // Scroll the schedule list so the currently-airing program sits at the
    // top of its (capped-height) scroll area. No-op when nothing is on air
    // — e.g. a non-today day, or between programs.
    scrollToAiringSchedule() {
      this.$nextTick(() => {
        const container = this.$refs.scheduleScroll
        if (!container) return
        const row = container.querySelector('[data-airing="true"]')
        if (!row) return
        container.scrollTop += row.getBoundingClientRect().top - container.getBoundingClientRect().top
      })
    },

    getLiveName(liveId) {
      if (!liveId) return '—'
      return this.livesMap[liveId?.toString()] || liveId
    },

    // Convert schedule time to a local Date for today
    scheduleTimeToLocal(schedule, which) {
      const tz = schedule.timezone || 'America/New_York'
      if (schedule.recurrency === 'recurrent') {
        const t = which === 'start' ? schedule.start_time : schedule.end_time
        if (!t || typeof t !== 'object' || t.hours === undefined) return null
        // Build a date string as if in the schedule's timezone
        const now = new Date()
        const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}T${String(t.hours).padStart(2, '0')}:${String(t.minutes || 0).padStart(2, '0')}:00`
        // sourceDate is parsed as local, we need to shift it to account for tz difference
        const sourceDate = new Date(dateStr)
        const sourceOffset = this.getTzOffsetMs(tz, sourceDate)
        const localOffset = sourceDate.getTimezoneOffset() * -60000
        const diff = localOffset - sourceOffset
        return new Date(sourceDate.getTime() + diff)
      }
      // One-time: start_datetime/end_datetime are stored as UTC-ish, interpret in schedule tz
      const dt = which === 'start' ? schedule.start_datetime : schedule.end_datetime
      if (!dt) return null
      return new Date(dt)
    },

    getTzOffsetMs(tz, date) {
      const utcStr = date.toLocaleString('en-US', { timeZone: 'UTC' })
      const tzStr = date.toLocaleString('en-US', { timeZone: tz })
      return new Date(tzStr).getTime() - new Date(utcStr).getTime()
    },

    formatScheduleTime(schedule, which) {
      const d = this.scheduleTimeToLocal(schedule, which)
      if (!d) return '—'
      const h = d.getHours()
      const m = String(d.getMinutes()).padStart(2, '0')
      const ampm = h >= 12 ? 'PM' : 'AM'
      const h12 = h % 12 || 12
      return `${String(h12).padStart(2, '0')}:${m} ${ampm}`
    },

    formatDuration(schedule) {
      const start = this.scheduleTimeToLocal(schedule, 'start')
      const end = this.scheduleTimeToLocal(schedule, 'end')
      if (!start || !end) return '—'
      let diff = Math.round((end.getTime() - start.getTime()) / 60000)
      if (diff <= 0) diff += 1440
      const h = Math.floor(diff / 60)
      const m = diff % 60
      if (h > 0 && m > 0) return `${h}h ${m}m`
      if (h > 0) return `${h}h`
      return `${m}m`
    },

    isCurrentlyAiring(schedule) {
      if (!this.isToday) return false
      const now = Date.now()
      const start = this.scheduleTimeToLocal(schedule, 'start')
      const end = this.scheduleTimeToLocal(schedule, 'end')
      if (!start || !end) return false
      return now >= start.getTime() && now <= end.getTime()
    },

    toggleOriginalTime(id) {
      this.showOriginalTime = { ...this.showOriginalTime, [id]: !this.showOriginalTime[id] }
    },

    formatOriginalTime(schedule, which) {
      if (schedule.recurrency === 'recurrent') {
        const t = which === 'start' ? schedule.start_time : schedule.end_time
        if (t && typeof t === 'object' && t.hours !== undefined) {
          const h = t.hours
          const m = String(t.minutes || 0).padStart(2, '0')
          const ampm = h >= 12 ? 'PM' : 'AM'
          const h12 = h % 12 || 12
          return `${String(h12).padStart(2, '0')}:${m} ${ampm}`
        }
        return '—'
      }
      const dt = which === 'start' ? schedule.start_datetime : schedule.end_datetime
      if (!dt) return '—'
      try {
        return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: schedule.timezone || 'America/New_York' }).format(new Date(dt))
      } catch { return '—' }
    },

    getShortTz(tz) {
      try {
        const part = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' }).formatToParts(new Date()).find(p => p.type === 'timeZoneName')
        return part?.value || tz
      } catch { return tz }
    },

    isSchedulePast(schedule) {
      if (!this.isToday) return false
      const end = this.scheduleTimeToLocal(schedule, 'end')
      if (!end) return false
      return Date.now() > end.getTime()
    }
  }
}
</script>
